import React, {useState} from "react";
import {useEffectOnMount, useNavigateByName, useProfileData, useUserCode} from "../../../../../hooks";
import {
  Block,
  Container,
  CopyToClipboard,
  Heading,
  Image,
  PrimaryHeading,
  Section,
  WithinBlockDecorator
} from "../../../../../components";
import {HeadingType} from "../../../../../components/common/typography/headings/Heading/Heading";
import {RouteDictionary} from "../../../../../utils";
import config from "../../../../../configs/app";
import {QRCodeSVG} from "qrcode.react";
import lucideIcon from 'assets/images/svg/lucide-icon.svg';

import styles from './Deposit.module.scss';


const requisites = [
  {label: "№ счёта", value: config.depositAccount},
  {label: "Банк", value: config.depositBank},
  {label: "БИК", value: config.depositBIC},
  {label: "ИНН", value: config.depositINN},
  {label: "КПП", value: config.depositKPP},
  {label: "Наименование получателя", value: config.depositName},
  {label: "Корр. счет", value: config.correspAcc},
];

const faqItems = [
  {
    question: "Минимальный размер пополнения?",
    answer: "1000 рублей.",
  },
  {
    question: "Максимальный размер пополнения?",
    answer: "без ограничений",
  },
  {
    question: "Минимальный размер инвестирования в сделку?",
    answer: "10 000 рублей.",
  },
  {
    question: "Минимальный размер инвестирования в сделку?",
    answer: "не более доступного остатка в сумме сделки",
  },
  {
    question: "Максимальный размер инвестирования на Платформе?",
    answer: (
      <>
        Для квалифицированных инвесторов и ИП - без ограничений.
        <br />
        Для физ.лиц, не имеющих статус квалифицированного инвестора - не более
        600 000 рублей (учитывается суммарный размер инвестирования на всех
        инвестиционных платформах, действующих в соответствии с ФЗ 259 от 2
        августа 2019 г. &quot;О привлечении инвестиций с использованием
        инвестиционных платформ и о внесении изменений в отдельные
        законодательные акты Российской Федерации&quot;).
      </>
    ),
  },
  {
    question: "Срок зачисления средств на номинальный счёт?",
    answer:
        "Срок зачисления зависит от срока проведения транзакции между банками, и как правило составляет не более трёх рабочих дней.",
  },
  {
    question: "Что такое Номинальный счёт?",
    answer:
        "Особый вид банковского счёта, предназначенный для хранения средств, права на которые принадлежат третьим лицам (инвесторам).",
  },
  {
    question: "Как Платформа понимает, что это мои деньги?",
    answer: (
      <>
        Идентификация платежа производится путём сверки данных, предоставляемых
        банком, с данными вашей регистрации. Идентификаторы данных: ваш ИНН (
        ОГРНИП для ИП), назначение платежа (указанный вами ID на Платформе).
      </>
    ),
  },
  {
    question: "Как я пойму, что средства зачислены?",
    answer:
        "Зачисленные средства отображаются у вас в Личном кабинете.",
  },
  {
    question: "Может ли Платформа отказать в зачислении?",
    answer: (
      <>
        Да, если:
        <ul>
          <li>вы осуществили платёж с чужого счёта (счёта, принадлежащего третьему лицу);</li>
          <li>
            неправильно указаны данные в назначении платежа, и/или Платформа не
            имеет возможности идентифицировать платёж;
          </li>
          <li>
            имеются основания для отказа в соответствии с требованиями
            Федерального закона &quot;О противодействии легализации (отмыванию)
            доходов, полученных преступным путём, и финансированию терроризма&quot;
            от 07.08.2001 N 115-ФЗ;
          </li>
          <li>в иных случаях, указанных в Правилах Платформы;</li>
        </ul>
      </>
    ),
  },
];

const Deposit = () => {
  const profileData = useProfileData();
  const navigate = useNavigateByName();
  const userCode = useUserCode();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAnswer = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const purpose = `Пополнение номинального счёта в рамках ФЗ 259 от 02.08.2019. Инвестор ИД ${profileData.paymentCode}, НДС 0`;
  const getQrCodeString = () => {
    return `ST00012|Name=${config.depositName}` +
            `|BIC=${config.depositBIC}` +
            `|PersonalAcc=${config.depositAccount}` +
            `|BankName=${config.depositBank}` +
            `|PayeeINN=${config.depositINN}` +
            `|KPP=${config.depositKPP}` +
            `|Purpose=${purpose}` +
            `|CorrespAcc=${config.correspAcc}`;
  };
  const canDeposit = () => {
    return profileData.isVerified;
  };
  useEffectOnMount(() => {
    if (!canDeposit()) {
      navigate(RouteDictionary.MY_MONEY);
      return;
    }
  }
  );
  return (
    <Container>
      <Section className={styles.depositSection}>
        <div className={styles.depositContainer}>
          <div className={styles.deposit}>
            <PrimaryHeading>
              <Heading headingType={HeadingType.H1}>Пополнение счета</Heading>
            </PrimaryHeading>
            <div className={styles.deposit__text}>
              Пополнение средств на Платформе осуществляется исключительно переводом средств с банковского счёта
              Инвестора на номинальный счёт Оператора Платформы
            </div>
            <div className={styles.deposit__text}>
              При пополнении счёта должно быть указано соответствующее назначение платежа
            </div>
            <div className={styles.deposit__data}>
              <Block>
                <Block.Content>
                  <Block.Header withPadding>Реквизиты</Block.Header>
                  <Block.Body withPadding>
                    <div className={styles.deposit__requisites}>
                      {requisites.map(({label, value}) => (
                        <div key={label} className={styles.deposit__requisite}>
                          <div>
                            <div className={styles.deposit__heading}>{label}</div>
                            <div>{value}</div>
                          </div>
                          <div>
                            <CopyToClipboard textToCopy={value}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Block.Body>
                </Block.Content>
              </Block>
              <div className={styles.deposit__purposeContainer}>
                <div className={styles.deposit__purpose}>
                  <div className={styles.deposit__purposTitle}>
                    Назначение платежа
                  </div>
                  <div className={styles.deposit__purposeText}>
                    При пополнении счета в назначении платежа укажите свой ID
                  </div>
                  <div className={styles.deposit__purposeBlock}>
                    <div className={styles.deposit__purposeId}>
                      Пополнение номинального счёта в рамках ФЗ 259 от 02.08.2019.<br/> Инвестор ИД {userCode}, НДС 0
                    </div>
                    <CopyToClipboard textToCopy={userCode} />
                  </div>
                </div>
                <div className={styles.deposit__qr}>
                  <div className={styles.deposit__qrInfo}>
                    <div>Пополнение по QR-коду</div>
                    <div className={styles.deposit__qrText}>
                      QR-код для пополнения счета
                    </div>
                  </div>
                  <div className={styles.deposit__qrBlock}>
                    <div className={styles.deposit__qrCode}>
                      <WithinBlockDecorator>
                        <QRCodeSVG size={250} level={"L"} value={getQrCodeString()}/>
                      </WithinBlockDecorator>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.faq}>
            <PrimaryHeading>
              <Heading headingType={HeadingType.H2} className={styles.faq__title}>FAQ</Heading>
            </PrimaryHeading>
            <div className={styles.faq__list}>
              {faqItems.map(({question, answer}, index) => (
                <div key={index} className={styles.faq__item}>
                  <div className={styles.faq__question} onClick={() => toggleAnswer(index)}>
                    <Image src={lucideIcon} alt="help-circle" onClick={() => toggleAnswer(index)}/>
                    {question}
                  </div>
                  {openIndex === index && <div className={styles.faq__answer}>{answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </Container>
  );
};

export default Deposit;